import { Observable, Observer, shareReplay } from 'rxjs';
import { Behavior } from '../util/behavior';
import { FileReader } from '../util/file-reader';
import { Queue } from '../util/queue';
import { UploadFile } from './file';
import { UploadJob } from './job';
import { UploadResponse } from './response';

type Status = 'processing' | 'done' | 'all-done' | 'error';

export class UploadBehavior extends Behavior {
  // default is 256KB
  public static chunkBytes: number = 256 * 1024;

  private _queue = new Queue<UploadJob>();

  public afterConnectedServer() {
    this._queue.run(this._uploadToServer.bind(this));
  }

  public upload(method: string, ...args: any[]): Observable<UploadResponse> {
    return Observable.create((observer: Observer<UploadResponse>) => {
      const job = new UploadJob(method, args, observer);
      this._queue.add(job);

      if (this.client.isReady()) {
        this.client.checkQueryLimit(job, this._uploadToServer.bind(this));
      }
    }).pipe(shareReplay(1));
  }

  private _uploadToServer(job: UploadJob) {
    const { path, jobId } = job;
    let { args } = job;
    const files: UploadFile[] = [];
    const sizes = [];
    const key = `${path}/${jobId}`;

    let fileIndex = 0;
    args = args.map((arg) => {
      if (arg.__proto__ !== File.prototype) return arg;
      const file = (files[fileIndex] = arg as UploadFile);
      file.index = fileIndex;
      file.offset = 0;
      sizes[fileIndex] = file.size;
      fileIndex++;

      return { size: file.size, name: file.name, type: file.type };
    });

    this.client.io.emit('upload_init', path, args, jobId);
    this.client.io.off(key);
    this.client.io.on(key, (status: Status, data: UploadResponse<any>) =>
      this._uploadToServerCallback(job, files, status, data),
    );
    files.map((file) => this._uploadNextBuffer(path, jobId, file));
  }

  private _uploadToServerCallback(
    job: UploadJob,
    files: UploadFile[],
    status: Status,
    data: UploadResponse,
  ) {
    const { path, jobId, observer } = job;

    if (observer.closed) {
      this.client.io.emit(`upload_cancel`, path, jobId);
      return;
    }
    switch (status) {
      case 'processing': {
        const file = files[data.fileIndex ?? 0];
        if (file === undefined) return;

        observer.next({
          status: 'processing',
          fileIndex: data?.fileIndex,
          file,
          percentage: data?.percentage,
          finished: data?.finished,
        });
        this._uploadNextBuffer(path, jobId, file);
        return;
      }

      case 'done': {
        const file = data.fileIndex ? files[data.fileIndex] : undefined;
        if (file === undefined) return;

        observer.next({
          status: 'done',
          fileIndex: data.fileIndex,
          file,
          percentage: 100,
        });
        return;
      }

      case 'all-done':
        observer.next({ status: 'all-done', percentage: 100, result: data });
        observer.complete?.();
        return;

      case 'error':
        return observer.error?.(data);
    }
  }

  private _uploadNextBuffer(method: string, jobId: string, file: UploadFile) {
    const reader: FileReader = new FileReader();
    const end = Math.min(file.offset + UploadBehavior.chunkBytes, file.size);
    const buffer = file.slice(file.offset, end);
    file.offset = end;
    const fileIndex = file.index;
    reader.onload = () =>
      this.client.io.emit(
        'upload_processing',
        method,
        fileIndex,
        reader.result,
        jobId,
      );
    reader.readAsArrayBuffer(buffer);
  }
}
